import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import MetricsCard from './MetricsCard';

const CustomerSupportPanel = () => {
  const [tickets, setTickets] = useState([
    {
      id: 'TK-001',
      subject: 'Unable to upload portfolio images',
      customer: 'Rajesh Photography',
      email: 'rajesh@example.com',
      status: 'open',
      priority: 'high',
      category: 'technical',
      createdAt: '2025-07-12T10:30:00Z',
      lastUpdated: '2025-07-12T12:15:00Z'
    },
    {
      id: 'TK-002',
      subject: 'Payment gateway not working',
      customer: 'Priya Mehndi Art',
      email: 'priya@example.com',
      status: 'in-progress',
      priority: 'high',
      category: 'billing',
      createdAt: '2025-07-12T09:20:00Z',
      lastUpdated: '2025-07-12T11:45:00Z'
    },
    {
      id: 'TK-003',
      subject: 'Profile verification pending',
      customer: 'Dream Decorators',
      email: 'info@dreamdeco.com',
      status: 'pending',
      priority: 'medium',
      category: 'account',
      createdAt: '2025-07-12T08:15:00Z',
      lastUpdated: '2025-07-12T08:15:00Z'
    }
  ]);

  const [selectedTicket, setSelectedTicket] = useState(null);
  const [newTicket, setNewTicket] = useState({
    subject: '',
    customer: '',
    email: '',
    priority: 'medium',
    category: 'general',
    description: ''
  });

  const ticketStats = {
    total: tickets.length,
    open: tickets.filter(t => t.status === 'open').length,
    inProgress: tickets.filter(t => t.status === 'in-progress').length,
    resolved: tickets.filter(t => t.status === 'resolved').length
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-red-600 bg-red-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'low': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'text-red-600 bg-red-100';
      case 'in-progress': return 'text-blue-600 bg-blue-100';
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      case 'resolved': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const handleStatusUpdate = (ticketId, newStatus) => {
    setTickets(prev => prev.map(ticket => 
      ticket.id === ticketId 
        ? { ...ticket, status: newStatus, lastUpdated: new Date().toISOString() }
        : ticket
    ));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-6 text-white">
        <h2 className="font-heading font-bold text-2xl mb-2">
          Customer Support Tickets
        </h2>
        <p className="text-blue-100">
          Manage and resolve customer support requests efficiently
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <MetricsCard
          title="Total Tickets"
          value={ticketStats.total}
          icon="Ticket"
          color="blue"
        />
        <MetricsCard
          title="Open Tickets"
          value={ticketStats.open}
          icon="AlertCircle"
          color="red"
        />
        <MetricsCard
          title="In Progress"
          value={ticketStats.inProgress}
          icon="Clock"
          color="orange"
        />
        <MetricsCard
          title="Resolved Today"
          value={ticketStats.resolved}
          icon="CheckCircle"
          color="green"
        />
      </div>

      {/* Tickets List */}
      <div className="bg-white rounded-xl shadow-soft">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="font-heading font-semibold text-xl text-text-primary">
              Recent Tickets
            </h3>
            <Button variant="primary">
              <Icon name="Plus" size={16} className="mr-2" />
              New Ticket
            </Button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ticket ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Subject
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Priority
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {tickets.map((ticket) => (
                <tr key={ticket.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {ticket.id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {ticket.subject}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div>
                      <div className="font-medium">{ticket.customer}</div>
                      <div className="text-gray-500">{ticket.email}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(ticket.priority)}`}>
                      {ticket.priority}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(ticket.status)}`}>
                      {ticket.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <Button variant="ghost" size="sm">
                      <Icon name="Eye" size={16} />
                    </Button>
                    <Button variant="ghost" size="sm">
                      <Icon name="MessageSquare" size={16} />
                    </Button>
                    <Button variant="ghost" size="sm">
                      <Icon name="Edit" size={16} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CustomerSupportPanel;